import { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { cases } from "@/lib/endpoints";
import { CASE_TYPE_N400 } from "@/lib/constants";

/**
 * /cases/new — creates a draft N-400 case for the signed-in user, then
 * redirects to the gating wizard.
 *
 * No UI: just kicks off the mutation on mount. The dashboard "New
 * application" button links here. If the mutation is already in flight
 * (StrictMode double-invokes effects in dev) we guard with a ref.
 */
export function NewCasePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const startedRef = useRef(false);

  const mutation = useMutation({
    mutationFn: () => cases.create({ case_type: CASE_TYPE_N400 }),
    onSuccess: (c) => {
      // Mark the dashboard list stale so a back-nav within the 30s
      // staleTime window still shows the freshly-created case.
      qc.invalidateQueries({ queryKey: ["cases"] });
      navigate(`/cases/${c.id}/workspace`, { replace: true });
    },
  });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    mutation.mutate();
  }, [mutation]);

  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500">
      {mutation.isError ? (
        <div className="text-center">
          <p className="text-red-600 font-medium">Could not start a new case.</p>
          <p className="text-sm text-slate-500 mt-1">
            {(mutation.error as Error).message}
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            ← Back to dashboard
          </button>
        </div>
      ) : (
        <span>Starting your application…</span>
      )}
    </div>
  );
}
